import { useEffect, useState } from 'react';
import { salaryConfigApi, type SalaryAllowance } from '../lib/api/salary-config';

interface Props {
  open: boolean;
  editing?: SalaryAllowance | null;
  onClose: () => void;
  onSaved: () => void;
}

const TAX_FREE_OPTIONS = ['Có (miễn thuế)', 'Không', 'Có phần miễn'];
const BH_OPTIONS = ['Có (đóng BH)', 'Không'];

function fmtVND(n: number): string {
  return n.toLocaleString('vi-VN');
}

export function AllowanceFormModal({ open, editing, onClose, onSaved }: Props) {
  const isEdit = Boolean(editing);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [taxFreeLabel, setTaxFreeLabel] = useState('Có (miễn thuế)');
  const [bhLabel, setBhLabel] = useState('Không');
  const [suggestedLabel, setSuggestedLabel] = useState('');
  const [defaultAmount, setDefaultAmount] = useState<string>('');
  const [active, setActive] = useState(true);
  const [sortOrder, setSortOrder] = useState<number>(0);

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setErr(null); setSaving(false); setDeleting(false);
    if (editing) {
      setCode(editing.code);
      setName(editing.name);
      setTaxFreeLabel(editing.taxFreeLabel);
      setBhLabel(editing.bhLabel);
      setSuggestedLabel(editing.suggestedLabel);
      setDefaultAmount(editing.defaultAmount != null ? String(editing.defaultAmount) : '');
      setActive(editing.active);
      setSortOrder(editing.sortOrder);
    } else {
      setCode(''); setName('');
      setTaxFreeLabel('Có (miễn thuế)'); setBhLabel('Không');
      setSuggestedLabel(''); setDefaultAmount('');
      setActive(true); setSortOrder(99);
    }
  }, [open, editing]);

  if (!open) return null;

  const amount = Number(defaultAmount.replace(/\D/g, '')) || 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const codeTrim = code.trim().toUpperCase();
    if (!isEdit && !/^[A-Z][A-Z0-9_]{0,9}$/.test(codeTrim)) {
      return setErr('Mã 1-10 ký tự, chữ hoa/số/_ (VD ANTRUA, XANG).');
    }
    if (name.trim().length < 2) return setErr('Tên phụ cấp tối thiểu 2 ký tự.');
    if (!suggestedLabel.trim()) return setErr('Nhập mức gợi ý (VD "730k/tháng" hoặc "Theo BC").');

    setSaving(true);
    try {
      if (isEdit && editing) {
        await salaryConfigApi.patchAllowance(editing.id, {
          name: name.trim(),
          taxFreeLabel,
          bhLabel,
          suggestedLabel: suggestedLabel.trim(),
          defaultAmount: amount || null,
          active,
          sortOrder,
        });
      } else {
        await salaryConfigApi.addAllowance({
          code: codeTrim,
          name: name.trim(),
          taxFreeLabel,
          bhLabel,
          suggestedLabel: suggestedLabel.trim(),
          defaultAmount: amount || null,
          active,
          sortOrder,
        });
      }
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!editing) return;
    if (!window.confirm(`Xoá phụ cấp ${editing.code} - ${editing.name}?\n\nCác NV đang áp dụng sẽ mất phụ cấp này ở kỳ lương tiếp theo.`)) return;
    setDeleting(true);
    setErr(null);
    try {
      await salaryConfigApi.removeAllowance(editing.id);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setDeleting(false); }
  };

  return (
    <div onClick={onClose} style={backdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalBox}>
        <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
          <div style={header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={headerIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><path d="M9 3h6v4H9z" /></svg>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#172033' }}>
                  {isEdit ? `Sửa phụ cấp ${editing?.code}` : 'Thêm phụ cấp mới'}
                </h3>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#667085' }}>
                  Master phụ cấp — áp dụng khi cấu hình lương từng NV
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} style={closeBtn}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>

          <div style={body}>
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
                <Field label="Mã phụ cấp" required>
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="ANTRUA"
                    disabled={isEdit}
                    maxLength={10}
                    style={{ ...input, fontWeight: 700, fontFamily: 'monospace' }}
                    required
                  />
                </Field>
                <Field label="Tên phụ cấp" required>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="VD Ăn trưa · Đi lại · Điện thoại" style={{ ...input, fontSize: 14 }} required />
                </Field>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Miễn thuế TNCN">
                  <select value={taxFreeLabel} onChange={(e) => setTaxFreeLabel(e.target.value)} style={input}>
                    {TAX_FREE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Đóng BHXH/BHYT/BHTN">
                  <select value={bhLabel} onChange={(e) => setBhLabel(e.target.value)} style={input}>
                    {BH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Mức gợi ý (nhãn hiển thị)" required>
                  <input value={suggestedLabel} onChange={(e) => setSuggestedLabel(e.target.value)} placeholder="VD 730k/tháng · Theo BC · Tự tính" style={input} required />
                </Field>
                <Field label="Mức mặc định (VND, optional)">
                  <div style={{ position: 'relative' }}>
                    <input
                      inputMode="numeric"
                      value={amount ? fmtVND(amount) : ''}
                      onChange={(e) => setDefaultAmount(e.target.value)}
                      placeholder="730000"
                      style={{ ...input, textAlign: 'right', paddingRight: 32, fontWeight: 600 }}
                    />
                    <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 13 }}>đ</span>
                  </div>
                </Field>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Thứ tự sắp xếp">
                  <input
                    type="number" min={0} max={999}
                    value={sortOrder}
                    onChange={(e) => setSortOrder(Math.max(0, Math.min(999, Number(e.target.value) || 0)))}
                    style={{ ...input, textAlign: 'right' }}
                  />
                </Field>
                <Field label="Trạng thái">
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 12px', border: '1px solid #e4e7ec', borderRadius: 8, cursor: 'pointer', fontSize: 13, height: 38, boxSizing: 'border-box' }}>
                    <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                    <span style={{ color: active ? '#166534' : '#64748b', fontWeight: 500 }}>
                      {active ? 'Đang bật (áp dụng)' : 'Tắt (không áp dụng NV mới)'}
                    </span>
                  </label>
                </Field>
              </div>

              <div style={hintBox}>
                <b>Ghi chú:</b> Sau khi tạo, phụ cấp sẽ xuất hiện trong form <b>Sửa NV</b> (mục "Lương & phụ cấp cố định") để tick cho từng NV.
              </div>

              {err && <div style={errBox}>{err}</div>}
            </div>
          </div>

          <div style={footer}>
            {isEdit ? (
              <button type="button" onClick={handleDelete} disabled={deleting || saving} style={deleteBtn}>
                {deleting ? 'Đang xoá…' : 'Xoá phụ cấp'}
              </button>
            ) : <div />}
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button type="button" onClick={onClose} disabled={saving || deleting} style={cancelBtn}>Huỷ</button>
              <button type="submit" disabled={saving || deleting} style={saveBtn}>
                {saving ? 'Đang lưu…' : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                    {isEdit ? 'Lưu thay đổi' : 'Lưu phụ cấp'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: '#334155', fontWeight: 600, marginBottom: 6 }}>
        {label} {required && <span style={{ color: '#dc2626' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)', backdropFilter: 'blur(4px)',
  zIndex: 60, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const modalBox: React.CSSProperties = {
  width: 620, maxWidth: '100%', maxHeight: 'calc(100vh - 48px)',
  display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 12,
  boxShadow: '0 25px 60px -12px rgba(15,23,42,.5)', overflow: 'hidden',
};
const header: React.CSSProperties = {
  flex: '0 0 auto', padding: '16px 22px', borderBottom: '1px solid #e4e7ec',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
const headerIcon: React.CSSProperties = {
  width: 38, height: 38, borderRadius: 9, background: 'linear-gradient(135deg,#eff6ff,#dbeafe)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const closeBtn: React.CSSProperties = {
  width: 32, height: 32, background: '#fff', border: '1px solid #e4e7ec', borderRadius: 8,
  cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const body: React.CSSProperties = { flex: 1, overflowY: 'auto', background: '#f8fafc', minHeight: 0 };
const footer: React.CSSProperties = {
  flex: '0 0 auto', padding: '12px 22px', borderTop: '1px solid #e4e7ec', background: '#fff',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
};
const input: React.CSSProperties = {
  width: '100%', height: 38, padding: '0 12px', border: '1px solid #e4e7ec',
  borderRadius: 8, fontSize: 13, color: '#172033', fontFamily: 'inherit',
  outline: 'none', boxSizing: 'border-box', background: '#fff',
};
const hintBox: React.CSSProperties = {
  padding: '10px 14px', background: '#eff6ff', borderLeft: '3px solid #2563EB',
  borderRadius: '0 6px 6px 0', fontSize: 12, color: '#1e3a8a', lineHeight: 1.5,
};
const errBox: React.CSSProperties = {
  padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5',
  borderRadius: 8, color: '#b91c1c', fontSize: 13,
};
const deleteBtn: React.CSSProperties = {
  padding: '9px 14px', background: '#fff', border: '1px solid #fca5a5', color: '#b91c1c',
  fontSize: 13, fontWeight: 500, borderRadius: 8, cursor: 'pointer',
};
const cancelBtn: React.CSSProperties = {
  padding: '9px 16px', background: '#fff', border: '1px solid #e4e7ec', color: '#334155',
  fontSize: 13, fontWeight: 500, borderRadius: 8, cursor: 'pointer',
};
const saveBtn: React.CSSProperties = {
  padding: '9px 20px', background: '#1d2939', color: '#fff', fontSize: 13, fontWeight: 600,
  border: 'none', borderRadius: 8, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
};
